import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { benchmarksApi } from "../lib/benchmarks";
import {
  type AddCashflowInput,
  type AddTransactionInput,
  type CashflowDto,
  type CountryWeight,
  type CountryWeightsLookupResponse,
  type CountryWeightsMutationResponse,
  type CountryWeightsResponse,
  type CreateInvestmentInput,
  type CreateManualQuoteInput,
  type InvestmentDetail,
  type InvestmentListItem,
  type InvestmentsSummary,
  type ListInvestmentsFilters,
  type PeriodOptions,
  type Quote,
  type QuoteRangeKey,
  type RefreshAllResponse,
  type RenditebrueckeOverridesInput,
  type TransactionDto,
  type UpdateCashflowInput,
  type UpdateInvestmentInput,
  type UpdateTransactionInput,
  investmentsApi,
} from "../lib/investments";

const baseKey = ["investments"] as const;

export function useInvestments(filters: ListInvestmentsFilters = {}) {
  return useQuery<{ investments: InvestmentListItem[] }>({
    queryKey: [...baseKey, "list", filters],
    queryFn: () => investmentsApi.list(filters),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useInvestmentDetail(id: string, period?: string) {
  return useQuery<InvestmentDetail>({
    queryKey: [...baseKey, "detail", id, period ?? "none"],
    queryFn: () => investmentsApi.get(id, period),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });
}

export function useInvestmentQuotes(id: string, range: QuoteRangeKey = "MAX") {
  return useQuery<{ quotes: Quote[] }>({
    queryKey: [...baseKey, "quotes", id, range],
    queryFn: () => investmentsApi.listQuotes(id, range),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });
}

export function useInvestmentsSummary(period?: string) {
  return useQuery<InvestmentsSummary>({
    queryKey: [...baseKey, "summary", period ?? "none"],
    queryFn: () => investmentsApi.summary(period),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function usePeriodOptions() {
  return useQuery<PeriodOptions>({
    queryKey: [...baseKey, "period-options"],
    queryFn: () => investmentsApi.periodOptions(),
    staleTime: 1000 * 60 * 60,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: baseKey });
}

export function useCreateInvestment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvestmentInput) => investmentsApi.create(input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateInvestment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateInvestmentInput }) =>
      investmentsApi.update(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateRenditebrueckeOverrides() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RenditebrueckeOverridesInput }) =>
      investmentsApi.updateRenditebrueckeOverrides(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSoftDeleteInvestment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => investmentsApi.softDelete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useHardDeleteInvestment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => investmentsApi.hardDelete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddManualQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateManualQuoteInput }) =>
      investmentsApi.addManualQuote(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, quoteId }: { id: string; quoteId: string }) =>
      investmentsApi.deleteQuote(id, quoteId),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRefreshQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => investmentsApi.refresh(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useRefreshAll() {
  const qc = useQueryClient();
  return useMutation<RefreshAllResponse>({
    mutationFn: async () => {
      const results = await Promise.allSettled([
        investmentsApi.refreshAll(),
        benchmarksApi.refreshAll(),
      ]);

      const investmentsResult = results[0];

      // Return the investments result (to maintain backwards compatibility).
      // Benchmark failure doesn't block the investment refresh.
      if (investmentsResult.status === "fulfilled") {
        return investmentsResult.value;
      }

      // If investments refresh fails, throw the error.
      if (investmentsResult.status === "rejected") {
        throw investmentsResult.reason;
      }

      // Fallback (should not reach here due to TypeScript exhaustiveness).
      throw new Error("Unexpected Promise.allSettled state");
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useBackfillQuotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: { investmentIds?: string[]; mode?: "incremental" | "full" } = {}) =>
      investmentsApi.backfillQuotes(params),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useCashflows(investmentId: string, enabled: boolean) {
  return useQuery<{ cashflows: CashflowDto[] }>({
    queryKey: ["cashflows", investmentId],
    queryFn: () => investmentsApi.listCashflows(investmentId),
    enabled,
  });
}

export function useAddCashflow(investmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddCashflowInput) => investmentsApi.addCashflow(investmentId, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cashflows", investmentId] }),
  });
}

export function useUpdateCashflow(investmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; patch: UpdateCashflowInput }) =>
      investmentsApi.updateCashflow(investmentId, p.id, p.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cashflows", investmentId] }),
  });
}

export function useDeleteCashflow(investmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => investmentsApi.deleteCashflow(investmentId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cashflows", investmentId] }),
  });
}

export function useTransactions(investmentId: string, enabled: boolean) {
  return useQuery<{ transactions: TransactionDto[] }>({
    queryKey: ["transactions", investmentId],
    queryFn: () => investmentsApi.listTransactions(investmentId),
    enabled,
  });
}

function invalidateAfterTransaction(qc: ReturnType<typeof useQueryClient>, investmentId: string) {
  qc.invalidateQueries({ queryKey: ["transactions", investmentId] });
  qc.invalidateQueries({ queryKey: baseKey });
}

export function useAddTransaction(investmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AddTransactionInput) => investmentsApi.addTransaction(investmentId, input),
    onSuccess: () => invalidateAfterTransaction(qc, investmentId),
  });
}

export function useUpdateTransaction(investmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { id: string; patch: UpdateTransactionInput }) =>
      investmentsApi.updateTransaction(investmentId, p.id, p.patch),
    onSuccess: () => invalidateAfterTransaction(qc, investmentId),
  });
}

export function useDeleteTransaction(investmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => investmentsApi.deleteTransaction(investmentId, id),
    onSuccess: () => invalidateAfterTransaction(qc, investmentId),
  });
}

// ============== Country Weights ==============

function invalidateAfterCountryWeights(
  qc: ReturnType<typeof useQueryClient>,
  investmentId: string,
) {
  void qc.invalidateQueries({ queryKey: [...baseKey, "country-weights", investmentId] });
  void qc.invalidateQueries({ queryKey: ["allocation"] });
}

export function useCountryWeights(investmentId: string) {
  return useQuery<CountryWeightsResponse>({
    queryKey: [...baseKey, "country-weights", investmentId],
    queryFn: () => investmentsApi.getCountryWeights(investmentId),
    enabled: Boolean(investmentId),
  });
}

export function useSetCountryWeights(investmentId: string) {
  const qc = useQueryClient();
  return useMutation<CountryWeightsMutationResponse, Error, CountryWeight[]>({
    mutationFn: (weights) => investmentsApi.setCountryWeights(investmentId, weights),
    onSuccess: () => invalidateAfterCountryWeights(qc, investmentId),
  });
}

export function useAutoLookupCountryWeights(investmentId: string) {
  const qc = useQueryClient();
  return useMutation<CountryWeightsLookupResponse>({
    mutationFn: () => investmentsApi.autoLookupCountryWeights(investmentId),
    onSuccess: () => invalidateAfterCountryWeights(qc, investmentId),
  });
}

export function useDeleteCountryWeights(investmentId: string) {
  const qc = useQueryClient();
  return useMutation<void>({
    mutationFn: () => investmentsApi.deleteCountryWeights(investmentId),
    onSuccess: () => invalidateAfterCountryWeights(qc, investmentId),
  });
}
